// src/Signup.jsx
import React, { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import { Eye, EyeOff, Mail, Lock, User, Building2, Phone, UserPlus } from 'lucide-react'

const API_BASE = import.meta.env.VITE_API_BASE

// Full country list with flags and dialing codes
const COUNTRIES = [
  { name: 'United States', code: 'US', dial: '1' },
  { name: 'United Kingdom', code: 'GB', dial: '44' },
  { name: 'Israel', code: 'IL', dial: '972' },
  { name: 'Germany', code: 'DE', dial: '49' },
  { name: 'France', code: 'FR', dial: '33' },
  { name: 'Japan', code: 'JP', dial: '81' }
]

// Prepare options for react-select with small flags
const countryOptions = COUNTRIES.map(c => ({
  value: c.dial,
  label: (
    <div className="flex items-center">
      <img
        src={`https://flagcdn.com/16x12/${c.code.toLowerCase()}.png`}
        alt={c.name}
        className="w-4 h-3 mr-2"
      />
      <span className="text-sm">+{c.dial}</span>
    </div>
  )
}))

// Styles ensuring dropdown matches input height
const selectStyles = {
  control: base => ({
    ...base,
    background: 'white',
    borderColor: 'var(--border-color)',
    boxShadow: 'none',
    minHeight: '2.5rem',
    height: '2.5rem',
    minWidth: '90px',
    '&:hover': { borderColor: 'var(--border-color)' }
  }),
  valueContainer: base => ({
    ...base,
    height: '2.5rem',
    padding: '0 0.5rem',
    flexWrap: 'nowrap'
  }),
  singleValue: base => ({
    ...base,
    color: 'var(--text-primary)',
    fontSize: '0.875rem',
    position: 'relative',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }),
  input: base => ({ ...base, margin: 0, padding: 0, color: 'var(--text-primary)' }),
  dropdownIndicator: base => ({
    ...base,
    padding: '0 4px',
    color: 'var(--text-primary)'
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: base => ({ ...base, background: 'white', color: 'var(--text-primary)' }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused ? 'var(--accent-primary-light)' : 'white',
    color: 'var(--text-primary)',
    '&:active': { background: 'var(--hover-light)' }
  })
}

const GoogleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z" fill="#4285F4"/>
    <path d="M12.24 24.0008C15.4764 24.0008 18.2058 22.9382 20.1944 21.1039L16.3274 18.1055C15.2516 18.8375 13.8626 19.252 12.24 19.252C9.07376 19.252 6.39287 17.1399 5.44288 14.3003H1.45801V17.3912C3.43223 21.4434 7.54226 24.0008 12.24 24.0008Z" fill="#34A853"/>
    <path d="M5.44277 14.3003C5.21387 13.5681 5.08188 12.7862 5.08188 12.0004C5.08188 11.2146 5.21387 10.4327 5.44277 9.70051V6.60962H1.45791C0.530724 8.24284 0 10.0682 0 12.0004C0 13.9326 0.530724 15.7579 1.45791 17.3912L5.44277 14.3003Z" fill="#FBBC05"/>
    <path d="M12.24 4.74966C14.0291 4.74966 15.6265 5.36715 16.8582 6.54066L20.2717 3.12721C18.2003 1.18941 15.4708 0 12.24 0C7.54226 0 3.43223 2.55737 1.45801 6.60962L5.44288 9.70051C6.39287 6.86095 9.07376 4.74966 12.24 4.74966Z" fill="#EA4335"/>
  </svg>
);

export default function Signup() {
  const [formData, setFormData] = useState({
    signupCode: '',
    email: '',
    password: '',
    companyName: '',
    fullName: '',
    phoneCountry: '1',
    phone: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState({})
  const [generalError, setGeneralError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [googleScriptLoaded, setGoogleScriptLoaded] = useState(false)
  const navigate = useNavigate()

  const handleGoogleResponse = useCallback(async (response) => {
    if (!response.credential) {
      setGeneralError('Google login failed. Please try again.')
      return
    }

    // Validate signup code again just in case
    if (!formData.signupCode) {
      setErrors(prev => ({ ...prev, signupCode: 'Signup code is required' }));
      document.getElementById('signupCode')?.focus();
      return;
    }

    setGoogleLoading(true)
    setGeneralError('')
    setErrors({})  // Clear any previous errors
    
    try {
      const res = await fetch(`${API_BASE}/auth/google?sentFrom=signup`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json;charset=UTF-8',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          credential: response.credential,
          provider: formData.signupCode
        })
      })
      
      const data = await res.json()
      
      if (res.ok) {
        // Check if response contains phone verification data
        if (data.phoneCountry && data.phone) {
          // Store phone information for SMS verification
          const phoneVerificationData = {
            PhoneCountry: data.phoneCountry,
            Phone: data.phone
          };
          
          // Include regToken if present
          if (data.regToken) {
            phoneVerificationData.regToken = data.regToken;
            sessionStorage.setItem('phoneVerificationData', JSON.stringify(phoneVerificationData));
            navigate('/sms-verification');
            return;
          }
        }

        // Check for registration token first
        if (data.regToken) {
          sessionStorage.setItem('registrationToken', data.regToken);
          navigate(`/signup/${data.regToken}`, { replace: true });
          return;
        }
        
        if (data.jwtToken) {
          // Store the JWT token and navigate to home
          sessionStorage.setItem('jwt', data.jwtToken);
          navigate('/home', { replace: true });
        } else if (data.token) {
          // Backward compatibility for old token field
          sessionStorage.setItem('jwt', data.token);
          navigate('/home', { replace: true });
        } else if (data.redirectUrl) {
          // Extract token from redirectUrl
          const registrationToken = data.redirectUrl.split('/').pop();
          
          // Save token for future use
          sessionStorage.setItem('registrationToken', registrationToken);
          
          // Check if phone is missing first to avoid unnecessary redirects
          try {
            console.log("Checking phone verification status directly...");
            const phoneResponse = await fetch(`${API_BASE}/signup/${registrationToken}/phonev`);
            
            if (phoneResponse.ok) {
              const phoneData = await phoneResponse.json();
              console.log("Phone verification direct check:", phoneData);
              
              // If phone is missing, go directly to update phone
              if (phoneData.missingPhone === true) {
                console.log("Missing phone detected, going directly to update phone");
                navigate(`/signup/${registrationToken}/updatephone`, { replace: true });
                return;
              }
            }
          } catch (err) {
            console.error("Error checking phone status:", err);
            // Continue with normal flow if this check fails
          }
          
          // Check registration state to determine next step
          fetch(`${API_BASE}/signup/${registrationToken}/check`)
            .then(response => {
              if (!response.ok) {
                throw new Error('Failed to check registration state');
              }
              return response.json();
            })
            .then(stateData => {
              console.log("Registration state after Google sign-in:", stateData);
              
              // Navigate based on registration state
              switch(stateData.state) {
                case 'InviteForm':
                  navigate(`/signup/${registrationToken}/invite`, { replace: true });
                  break;
                case 'MailVerification':
                  navigate(`/signup/${registrationToken}`, { replace: true });
                  break;
                case 'PhoneVerification':
                  navigate(`/signup/${registrationToken}/phoneverification`, { replace: true });
                  break;
                case 'Login':
                  navigate('/login', { replace: true });
                  break;
                default:
                  navigate(`/signup/${registrationToken}`, { replace: true });
              }
            })
            .catch(err => {
              console.error('Failed to check registration state:', err);
              // Fall back to basic redirect
              navigate(`/signup/${registrationToken}`, { replace: true });
            });
        } else {
          setGeneralError('Invalid response from server');
        }
      } else if (res.status === 401) {
        setErrors({ signupCode: 'Invalid signup code' });
        document.getElementById('signupCode')?.focus();
      } else {
        setGeneralError(data.message || 'Google login failed. Please try again.')
      }
    } catch (err) {
      setGeneralError('Unable to connect to the server. Please try again.')
    } finally {
      setGoogleLoading(false)
    }
  }, [navigate, formData.signupCode])

  useEffect(() => {
    // Clear any existing Google state and script on page load
    if (window.google?.accounts?.id) {
      window.google.accounts.id.cancel();
    }
    
    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Create fresh script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('Google script loaded successfully');
      if (window.google) {
        try {
          // Clear any existing configurations
          window.google.accounts.id.cancel();
          
          // Wait a brief moment before initializing
          setTimeout(() => {
            window.google.accounts.id.initialize({
              client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
              callback: handleGoogleResponse,
              auto_select: false,
              cancel_on_tap_outside: true,
              context: 'signin',
              itp_support: true,
              use_fedcm_for_prompt: true
            });
            setGoogleScriptLoaded(true);
            console.log('Google authentication initialized');
          }, 100);
        } catch (error) {
          console.error('Error initializing Google authentication:', error);
        }
      } else {
        console.error('Google API not available after script load');
      }
    };
    script.onerror = () => {
      console.error('Failed to load Google authentication script');
    };
    document.body.appendChild(script);

    return () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
      const script = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (script) {
        script.remove();
      }
    };
  }, []);

  const handleGoogleClick = () => {
    // Clear all previous errors first
    setErrors({});
    setGeneralError('');

    // Only validate signup code
    if (!formData.signupCode) {
      setErrors({ signupCode: 'Signup code is required' });
      document.getElementById('signupCode')?.focus();
      return;
    }

    console.log('Google button clicked');
    if (!window.google?.accounts?.id) {
      console.error('Google Sign-In not initialized');
      return;
    }

    try {
      console.log('Attempting Google Sign-In');
      // Cancel any existing prompt first
      window.google.accounts.id.cancel();

      // Configure One Tap and standard sign-in
      window.google.accounts.id.initialize({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
        context: 'signin'
      });
      
      // Create a hidden button container
      const buttonContainer = document.createElement('div');
      buttonContainer.style.display = 'none';
      document.body.appendChild(buttonContainer);

      // Render the button (it will be hidden)
      window.google.accounts.id.renderButton(
        buttonContainer,
        { type: 'standard' }
      );

      // Function to try the fallback method
      const tryFallbackMethod = () => {
        console.log('Executing fallback method');
        const button = buttonContainer.querySelector('div[role="button"]');
        if (button) {
          button.click();
        }
      };

      let oneTapTriggered = false;

      // Set a timeout to try fallback if One Tap doesn't respond
      const timeoutId = setTimeout(() => {
        if (!oneTapTriggered) {
          console.log('One Tap timeout - no response received');
          tryFallbackMethod();
        }
      }, 1000);

      // Try One Tap first
      console.log('Trying One Tap');
      window.google.accounts.id.prompt((notification) => {
        oneTapTriggered = true;
        clearTimeout(timeoutId);
        
        console.log('One Tap callback received:', notification);
        
        if (!notification) {
          console.log('No notification, trying fallback');
          tryFallbackMethod();
          return;
        }

        // Only try fallback for specific cases
        if (notification.isNotDisplayed()) {
          const reason = notification.getNotDisplayedReason();
          console.log('One Tap not displayed. Reason:', reason);
          tryFallbackMethod();
        } else if (notification.isSkippedMoment()) {
          const reason = notification.getSkippedReason();
          console.log('One Tap skipped. Reason:', reason);
          if (reason === 'user_cancel') {
            // User explicitly cancelled, don't show fallback
            console.log('User cancelled One Tap');
          } else {
            tryFallbackMethod();
          }
        } else if (notification.isDismissedMoment()) {
          const reason = notification.getDismissedReason();
          console.log('One Tap dismissed. Reason:', reason);
          if (reason === 'credential_returned') {
            // Successful sign-in, don't do anything
            console.log('Successful sign-in via One Tap');
          } else if (reason === 'flow_restarted') {
            // Flow is being restarted, don't do anything
            console.log('One Tap flow restarted');
          } else {
            tryFallbackMethod();
          }
        }
        // Don't handle unknown states - let One Tap continue
      });

      // Cleanup function
      const cleanup = () => {
        if (document.body.contains(buttonContainer)) {
          document.body.removeChild(buttonContainer);
        }
      };

      // Clean up after 5 seconds
      setTimeout(cleanup, 5000);

    } catch (error) {
      console.error('Error in Google Sign-In:', error);
      setGeneralError('Failed to show sign-in prompt. Please try again.');
    }
  };

  const handleChange = e => {
    const { name, value } = e.target
    // Map the new field names to the formData fields
    const fieldMap = {
      'new-fullname': 'fullName',
      'new-phone': 'phone'
    }
    const fieldName = fieldMap[name] || name
    setFormData(prev => ({ ...prev, [fieldName]: value }))
    setErrors(prev => ({ ...prev, [fieldName]: '' }))
    setGeneralError('')
  }

  const handleSelectChange = option => {
    setFormData(prev => ({ ...prev, phoneCountry: option.value }))
    setErrors(prev => ({ ...prev, phoneCountry: '' }))
  }

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove all non-digits
    setFormData(prev => ({ ...prev, phone: value }));
    setErrors(prev => ({ ...prev, phone: '' }));
  };

  const validate = () => {
    const newErrors = {};
    let isValid = true;

    // First check if fields are empty
    if (!formData.signupCode) {
      newErrors.signupCode = 'Signup code is required';
      isValid = false;
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
      isValid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    }

    if (!formData.companyName) {
      newErrors.companyName = 'Company name is required';
      isValid = false;
    }

    if (!formData.fullName) {
      newErrors.fullName = 'Full name is required';
      isValid = false;
    }

    // Then check validity if not empty
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    if (formData.password && (formData.password.length < 6 || !/^[\x20-\x7E]+$/.test(formData.password))) {
      newErrors.password = 'Use 6 or more characters with a mix of letters, numbers & symbols';
      isValid = false;
    }

    if (formData.companyName && formData.companyName.length < 3) {
      newErrors.companyName = 'Company name must be at least 3 characters';
      isValid = false;
    }

    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
      isValid = false;
    } else if (!/^\d+$/.test(formData.phone) || formData.phone.length < 7) {
      newErrors.phone = 'Invalid phone number';
      isValid = false;
    }

    setErrors(newErrors);

    // Focus on the first field with an error
    if (!isValid) {
      const firstErrorField = Object.keys(newErrors)[0];
      document.getElementById(firstErrorField)?.focus();
    }

    return isValid;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (loading || !validate()) return;
    setLoading(true);
    setGeneralError('');

    const submitData = {
      ...formData,
      provider: formData.signupCode,
      sentFrom: 'signup'
    };

    try {
      // First check if there's an ongoing registration
      const checkRes = await fetch(`${API_BASE}/signup/check?email=${encodeURIComponent(formData.email)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json;charset=UTF-8' }
      });

      const checkData = await checkRes.json().catch(() => ({}));

      if (checkRes.ok && checkData.registrationToken) {
        // There's an ongoing registration, redirect to it
        sessionStorage.setItem('registrationToken', checkData.registrationToken);
        navigate(`/signup/${checkData.registrationToken}`, { replace: true });
        return;
      }

      // No ongoing registration, proceed with signup
      const res = await fetch(`${API_BASE}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json;charset=UTF-8' },
        body: JSON.stringify(submitData)
      });

      const data = await res.json();

      if (res.ok) {
        if (data.redirectUrl) {
          // Extract registration token from redirectUrl
          const regToken = data.redirectUrl.split('/').pop();
          sessionStorage.setItem('registrationToken', regToken);
          
          // Check if phone is missing first to avoid unnecessary redirects
          try {
            console.log("Checking phone verification status directly...");
            const phoneResponse = await fetch(`${API_BASE}/signup/${regToken}/phonev`);
            
            if (phoneResponse.ok) {
              const phoneData = await phoneResponse.json();
              console.log("Phone verification direct check:", phoneData);
              
              // If phone is missing, go directly to update phone
              if (phoneData.missingPhone === true) {
                console.log("Missing phone detected, going directly to update phone");
                navigate(`/signup/${regToken}/updatephone`, { replace: true });
                return;
              }
            }
          } catch (err) {
            console.error("Error checking phone status:", err);
            // Continue with normal flow if this check fails
          }
          
          // Check registration state to determine next step
          fetch(`${API_BASE}/signup/${regToken}/check`)
            .then(response => {
              if (!response.ok) {
                throw new Error('Failed to check registration state');
              }
              return response.json();
            })
            .then(stateData => {
              console.log("Registration state after signup:", stateData);
              
              // Navigate based on registration state
              switch(stateData.state) {
                case 'InviteForm':
                  navigate(`/signup/${regToken}/invite`, { replace: true });
                  break;
                case 'MailVerification':
                  navigate(`/signup/${regToken}`, { replace: true });
                  break;
                case 'PhoneVerification':
                  navigate(`/signup/${regToken}/phoneverification`, { replace: true });
                  break;
                case 'Login':
                  navigate('/login', { replace: true });
                  break;
                default:
                  navigate(`/signup/${regToken}`, { replace: true });
              }
            })
            .catch(err => {
              console.error('Failed to check registration state:', err);
              // Fall back to basic redirect
              navigate(`/signup/${regToken}`, { replace: true });
            });
        } else if (data.jwtToken) {
          // Store JWT token and navigate to home
          sessionStorage.setItem('jwt', data.jwtToken);
          navigate('/home', { replace: true });
        } else {
          setGeneralError('An unexpected error occurred.');
        }
      } else if (res.status === 401) {
        setErrors({ signupCode: 'Invalid signup code' });
        document.getElementById('signupCode')?.focus();
      } else if (res.status === 400) {
        const data = await res.json().catch(() => ({}));
        Object.entries(data.errors || {}).forEach(([field, msgs]) => {
          setErrors(prev => ({ ...prev, [field]: msgs[0] }));
        });
      } else if (res.status === 402) {
        setGeneralError(
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-[var(--text-primary)] mb-4">You're already registered! Please log in to access your account.</p>
            <button
              onClick={() => navigate('/login')}
              className="text-[var(--accent-primary)] hover:text-[var(--hover-dark)] text-sm font-medium"
            >
              Go to login
            </button>
          </div>
        );
      } else {
        setGeneralError('An unexpected error occurred.');
      }
    } catch {
      setGeneralError('Unable to connect to the server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if we have an ongoing registration
    const regToken = sessionStorage.getItem('registrationToken');
    if (regToken) {
      navigate(`/signup/${regToken}`);
      return;
    }
    
    // Check if we have a saved invite token
    const inviteRegToken = localStorage.getItem('inviteRegToken');
    if (inviteRegToken) {
      navigate(`/signup/${inviteRegToken}/invite?from=email&invite=true`);
      return;
    }
  }, [navigate]);

  // Add a useEffect to watch for autofill changes
  useEffect(() => {
    // Create a MutationObserver to watch for autofill
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.target.matches('input[name="phone"]') && mutation.target.value) {
          // If phone was autofilled, check if we should update country code
          const phoneInput = mutation.target;
          if (phoneInput.value.startsWith('+')) {
            // Extract country code from autofilled phone number
            const countryCode = phoneInput.value.split(' ')[0].replace('+', '');
            setFormData(prev => ({
              ...prev,
              phoneCountry: countryCode,
              phone: phoneInput.value.split(' ').slice(1).join(' ') // Remove country code from phone
            }));
          }
        }
      });
    });

    // Start observing
    const phoneInput = document.querySelector('input[name="phone"]');
    if (phoneInput) {
      observer.observe(phoneInput, {
        attributes: true,
        characterData: true,
        childList: true,
        subtree: true
      });
    }

    return () => observer.disconnect();
  }, []);

  // Add this useEffect to watch for Chrome's autofill
  useEffect(() => {
    let timeoutId;
    
    const checkAutofill = () => {
      const phoneInput = document.querySelector('input[name="phone"]');
      const fullNameInput = document.querySelector('input[name="fullName"]');
      
      if (phoneInput && fullNameInput && fullNameInput.value && phoneInput.value) {
        // If both fields are filled (likely autofill), check for autofill background
        const autofillBackground = window.getComputedStyle(phoneInput).getPropertyValue('background-color');
        if (autofillBackground !== 'rgb(42, 62, 81)') { // Not our normal background color
          // Chrome has autofilled, try to update country code
          const countryCode = '972'; // Default to Israel if autofilled
          setFormData(prev => ({
            ...prev,
            phoneCountry: countryCode
          }));
        }
      }
    };

    // Check multiple times as autofill might take a moment
    timeoutId = setInterval(checkAutofill, 100);
    
    // Stop checking after 2 seconds
    setTimeout(() => {
      clearInterval(timeoutId);
    }, 2000);

    return () => {
      if (timeoutId) clearInterval(timeoutId);
    };
  }, []);

  return (
    <div className="w-full">
      <div className="w-full bg-white rounded-xl border border-[var(--border-color)] p-6" style={{ boxShadow: 'var(--card-shadow)' }}>
        <h2 className="text-2xl font-bold mb-3 text-[var(--accent-secondary)]">Sign Up</h2>
        {generalError && <p className="mb-3 text-red-500">{generalError}</p>}
        <form onSubmit={handleSubmit} noValidate className="space-y-3">
          {/* Signup Code Section */}
          <div className="bg-purple-50/80 p-4 rounded-lg border border-purple-100 mb-4">
            <div className="relative">
              <input
                type="text"
                name="signupCode"
                id="signupCode"
                value={formData.signupCode}
                onChange={handleChange}
                disabled={loading || googleLoading}
                autoComplete="off"
                placeholder="Enter your signup code"
                className={`w-full p-2 pl-10 pr-3 border rounded bg-white focus:outline-none ${
                  errors.signupCode 
                    ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                    : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
                } disabled:opacity-50`}
              />
              <User className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            </div>
            {errors.signupCode && <p className="mt-1 text-red-500 text-sm">{errors.signupCode}</p>}
          </div>

          {/* Email */}
          <div>
            <div className="relative">
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                disabled={loading || googleLoading}
                autoComplete="off"
                placeholder="Email"
                className={`w-full p-2 pl-10 pr-3 border rounded bg-white focus:outline-none ${
                  errors.email 
                    ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                    : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
                } disabled:opacity-50`}
              />
              <Mail className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            </div>
            {errors.email && <p className="mt-1 text-red-500 text-sm">{errors.email}</p>}
          </div>

          {/* Password */}
          <div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                id="password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading || googleLoading}
                placeholder="Password"
                className={`w-full p-2 pl-10 pr-9 border rounded bg-white focus:outline-none ${
                  errors.password 
                    ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                    : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
                } disabled:opacity-50`}
              />
              <Lock className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-2 flex items-center"
                tabIndex={-1}
                disabled={loading || googleLoading}
              >
                {showPassword ? <EyeOff className="w-5 h-5 text-[var(--text-primary)]" /> : <Eye className="w-5 h-5 text-[var(--text-primary)]" />}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-red-500 text-sm">{errors.password}</p>}
          </div>

          {/* Company Name */}
          <div>
            <div className="relative">
              <input
                type="text"
                name="companyName"
                id="companyName"
                value={formData.companyName}
                onChange={handleChange}
                disabled={loading || googleLoading}
                placeholder="Company Name"
                className={`w-full p-2 pl-10 pr-9 border rounded bg-white focus:outline-none ${
                  errors.companyName 
                    ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                    : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
                } disabled:opacity-50`}
              />
              <Building2 className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            </div>
            {errors.companyName && <p className="mt-1 text-red-500 text-sm">{errors.companyName}</p>}
          </div>

          {/* Full Name */}
          <div>
            <div className="relative">
              <input
                type="text"
                name="new-fullname"
                id="fullName"
                value={formData.fullName}
                onChange={handleChange}
                disabled={loading || googleLoading}
                autoComplete="new-fullname"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                data-form-type="other"
                placeholder="Full Name"
                className={`w-full p-2 pl-10 pr-9 border rounded bg-white focus:outline-none ${
                  errors.fullName 
                    ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                    : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
                } disabled:opacity-50`}
              />
              <User className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            </div>
            {errors.fullName && <p className="mt-1 text-red-500 text-sm">{errors.fullName}</p>}
          </div>

          {/* Phone */}
          <div>
            <div className="flex gap-2">
              <div className="shrink-0">
                <Select
                  options={countryOptions}
                  defaultValue={countryOptions.find(o => o.value === formData.phoneCountry)}
                  onChange={handleSelectChange}
                  isSearchable
                  styles={{
                    ...selectStyles,
                    control: (base) => ({
                      ...selectStyles.control(base),
                      borderColor: errors.phone ? 'rgb(239, 68, 68)' : 'var(--border-color)',
                      '&:hover': {
                        borderColor: errors.phone ? 'rgb(239, 68, 68)' : 'var(--border-color)'
                      }
                    })
                  }}
                  isDisabled={loading || googleLoading}
                />
              </div>
              <div className="relative flex-1">
                <input
                  type="text"
                  name="new-phone"
                  id="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  disabled={loading || googleLoading}
                  autoComplete="new-phone"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  data-form-type="other"
                  placeholder="Phone number"
                  className={`w-full p-2 pl-10 pr-9 h-10 border rounded bg-white focus:outline-none ${
                    errors.phone 
                      ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                      : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
                  } disabled:opacity-50`}
                />
                <Phone className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
              </div>
            </div>
            {errors.phoneCountry && <p className="mt-1 text-red-500 text-sm">{errors.phoneCountry}</p>}
            {errors.phone && <p className="mt-1 text-red-500 text-sm">{errors.phone}</p>}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center items-center px-4 py-2 font-semibold bg-[var(--accent-primary)] text-[var(--btn-text-on-accent)] rounded hover:bg-[var(--hover-dark)] active:bg-[var(--hover-dark)] focus-visible:bg-[var(--hover-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] focus:ring-offset-2 focus:ring-offset-[var(--card-bg)] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--accent-primary)]"
          >
            {loading ? 'Submitting...' : (
              <>
                <UserPlus className="w-5 h-5 mr-2" />
                Sign Up
              </>
            )}
          </button>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[var(--border-color)]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-[var(--text-secondary)]">Or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleClick}
            disabled={loading || googleLoading || !googleScriptLoaded}
            className="w-full flex justify-center items-center gap-2 px-4 py-2 font-medium border border-[var(--border-color)] rounded bg-white hover:bg-gray-50 active:bg-gray-50 focus-visible:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] focus:ring-offset-2 focus:ring-offset-white transition-colors duration-200 disabled:opacity-50"
          >
            <GoogleIcon />
            <span className="text-[var(--text-primary)]">Continue with Google</span>
          </button>
        </form>
      </div>
      
      {/* Footer section with minimal padding */}
      <div className="w-full max-w-sm sm:max-w-md mx-auto px-4 py-1 mt-1">
        <div className="text-center text-sm text-[var(--text-secondary)]">
        </div>
      </div>
    </div>
  )
}
